/* eslint-disable react/display-name */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import React from 'react';
import { Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import ReportLink from 'components/ReportsModule/components/common/ReportLink';
import getClassroomPrimaryEducator from 'utilities/get-classroom-primary-eduator';
import ReportCard from 'components/ReportsModule/components/common/ReportCard';
import LwtTable, { ColumnProps } from 'components/@common/Table/LwtTable';
import { KeyOrganizationClassroomEngagementReportResult } from 'types/schema-generated-types';
import { ReportEntityType } from '../../../../types';
import { TFunction } from 'i18next';
import BreakableTypography from 'components/@common/BreakableTypography';

const getColumns: (t: TFunction) => ColumnProps<any>[] = (t: TFunction) => [
  {
    id: 'name',
    label: t('Classroom'),
    sortKey: 'classroom.name',
    getValue: item => (
      <BreakableTypography>
        <ReportLink
          key={item.classroom.id}
          entityType={ReportEntityType.CLASSROOM}
          entityId={item.classroom.id}
          organizationId={item.classroom.organizationId}
        >
          {item.classroom.name}
        </ReportLink>
      </BreakableTypography>
    ),
    sortable: true
  },
  {
    id: 'educator',
    label: t('user'),
    sortKey: 'classroom.educator',
    getValue: item => {
      const educator = getClassroomPrimaryEducator(item.classroom);
      return (
        <Typography>
          <ReportLink
            key={item.classroom.id}
            entityType={ReportEntityType.USER}
            entityId={educator?.id}
          >
            {`${educator?.firstName} ${educator?.lastName}`}
          </ReportLink>
        </Typography>
      );
    },
    sortable: true
  },
  {
    id: 'totalStudentCount',
    label: t('Total Licensed Students'),
    helpText: t('totalStudents', { ns: 'ReportsHelpTableColumns' }),
    sortKey: 'totalStudentCount',
    getValue: item => <Typography>{item.totalStudentCount}</Typography>,
    sortable: true
  },
  {
    id: 'activeStudents',
    label: t('Active Students'),
    helpText: t('activeStudents', { ns: 'ReportsHelpTableColumns' }),
    sortKey: 'activeStudentCount',
    getValue: item => <Typography>{item.activeStudentCount}</Typography>,
    sortable: true
  },
  {
    id: 'curriculumProgressPercentage',
    label: t('Overall Curriculum Progress'),
    helpText: t('curriculumProgressBySchoolClass', { ns: 'ReportsHelpCommon' }),
    sortKey: 'curriculumProgressPercentage',
    getValue: item => <Typography>{item.curriculumProgressPercentage}%</Typography>,
    sortable: true
  },
  {
    id: 'totalActivitiesCompletedCount',
    label: t('Activities Completed'),
    helpText: t('activitiesCompletedBySchoolClass', { ns: 'ReportsHelpCommon' }),
    sortKey: 'totalActivitiesCompletedCount',
    getValue: item => <Typography>{item.totalActivitiesCompletedCount}</Typography>,
    sortable: true
  },
  {
    id: 'averageWeeklyMinutesSpent',
    label: t('Average Weekly Minutes Spent'),
    helpText: t('averageWeeklyTimeSpentSplit', { ns: 'ReportsHelpCommon' }),
    sortKey: 'averageWeeklyMinutesSpent',
    getValue: item => <Typography>{item.averageWeeklyMinutesSpent} minutes</Typography>
  }
];

const EngagementTableView: React.FC<{
  reportData: KeyOrganizationClassroomEngagementReportResult[];
  loading: boolean;
}> = ({ reportData = [], loading }) => {
  const { t } = useTranslation(['Reports', 'ReportsHelpCommon', 'ReportsHelpTableColumns']);
  const columns = getColumns(t);

  return (
    <ReportCard title={t('Overview')} loading={loading} reportData={reportData}>
      <LwtTable columns={columns} data={reportData} />
    </ReportCard>
  );
};

export default EngagementTableView;
